import SocialIcon from '@mui/icons-material/Campaign'
import CatalogIcon from '@mui/icons-material/MenuBook'
import PhotoFilterIcon from '@mui/icons-material/PhotoFilter'
import FeedIcon from '@mui/icons-material/RssFeed'
import SellIcon from '@mui/icons-material/Sell'
import SettingsIcon from '@mui/icons-material/Settings'
import { Theme, useMediaQuery } from '@mui/material'
import { FC, useState } from 'react'
import {
  DashboardMenuItem,
  LinearProgress,
  MenuItemLink,
  MenuProps,
  Menu as RaMenu,
  usePermissions,
  useSidebarState,
  useTranslate,
} from 'react-admin'
import { useSelector } from 'react-redux'
import {
  AppointmentIcon,
  BoatIcon,
  CategoryIcon,
  CustomerIcon,
  DocumentIcon,
  HistoryTransactionIcon,
  RoleIcon,
  SelectionIcon,
  UserIcon,
} from '~/resources'
import { AppState } from '../types'
import SubMenu from './SubMenu'

type MenuName =
  'administration' | 'customers' | 'boats' | 'marketing' | 'appointments' | 'documents'

const Menu: FC<MenuProps> = ({ onMenuClick, logout, dense = false }) => {
  // react-admin 4 replaced usePermissions()'s `loaded` with `isPending`.
  // Reading the old name left the guard permanently falsy, so the sidebar
  // rendered nothing but its progress bar.
  const { isPending, permissions } = usePermissions()
  const [state, setState] = useState({
    administration: true,
    customers: true,
    boats: true,
    marketing: true,
    appointments: true,
    documents: true,
  })

  const translate = useTranslate()

  const [open] = useSidebarState()
  useSelector((state: AppState) => state.theme) // force rerender on theme change

  const handleToggle = (menu: MenuName) => {
    setState((state) => ({ ...state, [menu]: !state[menu] }))
  }

  // todo create this object depending on introspection query result
  const menu = [
    {
      toggle: 'administration',
      icon: <RoleIcon />,
      role: 'admin',
      children: [
        {
          resource: 'users',
          icon: <UserIcon />,
        },
        {
          resource: 'history_transactions',
          icon: <HistoryTransactionIcon />,
        },
        {
          resource: 'selections',
          icon: <SelectionIcon />,
        },
      ],
    },
    {
      toggle: 'customers',
      icon: <CustomerIcon />,
      role: 'admin',
      children: [
        {
          resource: 'customers',
          icon: <CustomerIcon />,
        },
        {
          resource: 'suppliers',
          icon: <CustomerIcon />,
        },
      ],
    },
    {
      toggle: 'boats',
      icon: <BoatIcon />,
      role: 'admin',
      children: [
        {
          resource: 'boats',
          icon: <BoatIcon />,
        },
        {
          resource: 'categories',
          icon: <CategoryIcon />,
        },
      ],
    },
    {
      // Getting boats in front of buyers: photos, the portals, social media.
      toggle: 'marketing',
      icon: <SellIcon />,
      role: 'admin',
      children: [
        {
          resource: 'photos',
          text: 'photos.title',
          icon: <PhotoFilterIcon />,
        },
        {
          resource: 'feed',
          text: 'feed.title',
          icon: <FeedIcon />,
        },
        {
          resource: 'social',
          text: 'social.title',
          icon: <SocialIcon />,
        },
        {
          resource: 'catalog',
          text: 'catalog.title',
          icon: <CatalogIcon />,
        },
      ],
    },
    {
      toggle: 'appointments',
      icon: <AppointmentIcon />,
      role: 'admin',
      children: [
        {
          resource: 'appointments',
          icon: <AppointmentIcon />,
        },
      ],
    },
    {
      toggle: 'documents',
      icon: <DocumentIcon />,
      role: 'admin',
      children: [
        {
          resource: 'documents',
          icon: <DocumentIcon />,
        },
      ],
    },
    {
      resource: 'configuration',
      icon: <SettingsIcon />,
      text: 'pos.configuration',
      condition: useMediaQuery((theme: Theme) => theme.breakpoints.down('xs')),
    },
  ]

  const menuRender = (item) => {
    if (item.role && !(permissions ?? []).includes(item.role)) return null
    if (typeof item.condition !== 'undefined' && !item.condition) return null

    return item.toggle ? (
      <SubMenu
        key={item.key || item.toggle}
        handleToggle={() => handleToggle(item.toggle)}
        isOpen={state[item.toggle]}
        sidebarIsOpen={open}
        name={`resources.${item.toggle}.menu`}
        icon={item.icon}
        dense={dense}
      >
        {item.children.map(menuRender)}
      </SubMenu>
    ) : (
      <MenuItemLink
        key={item.key || item.resource}
        to={`/${item.resource}`}
        primaryText={translate(item.text || `resources.${item.resource}.title`)}
        leftIcon={item.icon}
        onClick={onMenuClick}
        sidebarIsOpen={open}
        dense={dense}
      />
    )
  }

  return !isPending ? (
    // MUI 9 throws "MenuListContext is missing" when a MenuItem has no
    // MenuList ancestor — MUI 5 only warned. react-admin's <Menu> is a styled
    // MenuList, so it supplies the context this used to do without.
    <RaMenu sx={{ mt: 1 }}>
      <DashboardMenuItem onClick={onMenuClick} sidebarIsOpen={open} />
      {menu.map(menuRender)}
    </RaMenu>
  ) : (
    <LinearProgress />
  )
}

export default Menu
